import { CommonModule } from '@angular/common';
import type { OnChanges, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { PipesModule } from '@freelancer/pipes';
import { UiModule } from '@freelancer/ui';
import { FileDisplayType } from '@freelancer/ui/file-display';
import {
  HeadingColor,
  HeadingType,
  HeadingWeight,
} from '@freelancer/ui/heading';
import { IconSize } from '@freelancer/ui/icon';
import { MediaViewerComponent } from '@freelancer/ui/media-viewer';
import type { AspectRatioValue } from '@freelancer/ui/picture';
import {
  PictureLayout,
  PictureLoadMethod,
  PictureObjectFit,
} from '@freelancer/ui/picture';
import { SpinnerSize } from '@freelancer/ui/spinner';
import { TextSize } from '@freelancer/ui/text';
import type { GalleryItem } from '../gallery-types/gallery.types';
import { GalleryItemType } from '../gallery-types/gallery.types';
import { RoundCornerType, SingleImageFit } from './gallery-mosaic.types';

@Component({
  standalone: true,
  selector: 'fl-gallery-mosaic-interactive-content',
  template: ` <ng-content></ng-content> `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GalleryMosaicInteractiveContentComponent {}

@Component({
  standalone: true,
  selector: 'fl-gallery-mosaic',
  imports: [CommonModule, MediaViewerComponent, PipesModule, UiModule],
  template: `
    <div
      class="GalleryGrid"
      [attr.center-content]="centerContent"
      [attr.item-spacing]="itemSpacing"
    >
      <div
        *ngFor="
          let galleryItem of galleryItems
            | slice: 0 : displayedGalleryItemsModern;
          index as i;
          let isFirst = first;
          trackBy: trackByItemSource
        "
        tabindex="0"
        [attr.item-spacing]="itemSpacing"
        [attr.aria-label]="galleryItem.fileName"
        [class.OneItem]="galleryItems.length === 1"
        [class.TwoItems]="galleryItems.length === 2"
        [class.ThreeItems]="galleryItems.length === 3"
        [class.FourItems]="galleryItems.length >= 4 && !forceRectangleMedium"
        [class.FiveItems]="galleryItems.length >= 5 && forceRectangleMedium"
        [class.ReverseGallery]="reverseGallery"
        [ngSwitch]="galleryItem.type"
        (click)="handleGalleryItemClicked(i, $event)"
      >
        <div>
          <ng-container *ngSwitchCase="GalleryItemType.PICTURE">
            <div
              *ngIf="!errorItems.includes(galleryItem)"
              class="GridColumn-pictureContainer"
              [attr.item-spacing]="itemSpacing"
              [attr.rounded-corners]="roundGalleryCorners"
              [class.InteractiveContent]="interactiveContent"
              [class.GridColumn-pictureContainer-oneItem]="
                singleImageFit === 'contain' && isSingleImageGallery
              "
            >
              <fl-picture
                *ngIf="isSingleImageGallery; else multipleImages"
                class="GridColumn-pictureContainer-picture"
                [class.ZoomOnHover]="zoomOnHover"
                [class.Clickable]="isViewerEnabled"
                [alignCenter]="true"
                [alt]="galleryItem.alt || galleryItem.fileName"
                [src]="galleryItem.source"
                [width]="galleryItem.width"
                [height]="galleryItem.height"
                [boundedWidth]="singleImageFit === 'contain'"
                [boundedHeight]="singleImageFit === 'contain'"
                [aspectRatioValue]="
                  singleImageFit === 'cover' ||
                  loadMethod === PictureLoadMethod.LAZY
                    ? firstGalleryItemAspectRatio
                    : undefined
                "
                [objectFit]="
                  singleImageFit === 'contain'
                    ? PictureObjectFit.CONTAIN
                    : PictureObjectFit.COVER
                "
                [layout]="usingFastlyIO ? PictureLayout.RESPONSIVE : undefined"
                [loadMethod]="loadMethod"
                [sizes]="usingFastlyIO ? imageSizes : undefined"
                (imageLoaded)="stopLoading(0)"
                (onError)="handleImageLoadError(galleryItem)"
              />
              <ng-template #multipleImages>
                <fl-picture
                  class="GridColumn-pictureContainer-picture"
                  [attr.rounded-corners]="roundGalleryCorners"
                  [class.ZoomOnHover]="zoomOnHover"
                  [class.Clickable]="isViewerEnabled"
                  [alignCenter]="true"
                  [alt]="galleryItem.alt || galleryItem.fileName"
                  [aspectRatioValue]="
                    isFirst
                      ? firstGalleryItemAspectRatio
                      : galleryItemAspectRatio
                  "
                  [borderRadiusTop]="
                    roundGalleryCorners === RoundCornerType.ITEM
                  "
                  [src]="galleryItem.source"
                  [objectFit]="PictureObjectFit.COVER"
                  [layout]="
                    usingFastlyIO ? PictureLayout.RESPONSIVE : undefined
                  "
                  [loadMethod]="loadMethod"
                  [sizes]="usingFastlyIO ? imageSizes : undefined"
                  (imageLoaded)="stopLoading(i)"
                  (onError)="handleImageLoadError(galleryItem)"
                ></fl-picture>
              </ng-template>
              <fl-link
                *ngIf="!!galleryItem.link"
                class="Link"
                [link]="galleryItem.link"
              />
              <div
                *ngIf="interactiveContent"
                class="ContentWrapper"
              >
                <div class="Content">
                  <ng-container
                    [ngTemplateOutlet]="galleryInteractiveContent"
                    [ngTemplateOutletContext]="{ $implicit: i }"
                  />
                </div>
              </div>
              <fl-spinner
                *ngIf="loadingItems[i]"
                class="GridColumn-pictureContainer-loading"
                [attr.rounded-corners]="roundGalleryCorners"
                [class.GridColumn-pictureContainer-loading-background]="
                  interactiveContent
                "
                [size]="SpinnerSize.SMALL"
              />
            </div>
            <div
              *ngIf="errorItems.includes(galleryItem)"
              class="GridColumn-pictureLoadError"
            >
              <div class="GridColumn-pictureLoadError-inner">
                <fl-icon
                  class="GalleryFileDisplay"
                  [name]="'ui-broken-image'"
                  [size]="IconSize.SMALL"
                  [sizeTablet]="IconSize.MID"
                  [sizeDesktop]="IconSize.XLARGE"
                  [flMarginBottom]="'xxxsmall'"
                />
                <span
                  class="text-foreground text-xxsmall text-center"
                  i18n="Image Failed To Load"
                >
                  Failed to load
                </span>
              </div>
            </div>
          </ng-container>
          <!--
              When the type is either GalleryItemType.FILE or GalleryItemType.VIDEO
            -->
          <div
            *ngSwitchDefault
            class="GridColumn-filePreview"
            [class.Clickable]="isViewerEnabled"
          >
            <div class="GridColumn-filePreview-inner">
              <fl-file-display
                class="GalleryFileDisplay"
                [alt]="galleryItem.fileName"
                [filename]="galleryItem.fileName"
                [iconSize]="IconSize.SMALL"
                [iconSizeTablet]="IconSize.MID"
                [iconSizeDesktop]="IconSize.XLARGE"
                [src]="galleryItem.source"
                [type]="FileDisplayType.ICON"
                [flMarginBottom]="'xxxsmall'"
              />
              <span class="text-foreground text-xxsmall text-center">
                {{ galleryItem.fileName | truncateFilename: 20 }}
              </span>
            </div>
          </div>
          <div
            *ngIf="
              i === displayedGalleryItemsModern - 1 &&
              galleryItems.length > displayedGalleryItemsModern &&
              !loadingItems[i]
            "
            class="GridColumn-cover"
            [attr.item-spacing]="itemSpacing"
            [attr.rounded-corners]="roundGalleryCorners"
            [class.Clickable]="isViewerEnabled"
          >
            <fl-heading
              class="ItemCounter"
              [color]="HeadingColor.LIGHT"
              [headingType]="HeadingType.H2"
              [size]="textSize ?? TextSize.LARGE"
              [sizeTablet]="textSizeTablet ?? TextSize.XLARGE"
              [sizeDesktop]="textSizeDesktop ?? TextSize.XXLARGE"
              [weight]="HeadingWeight.LIGHT"
            >
              + {{ galleryItems.length - displayedGalleryItemsModern }}
            </fl-heading>
          </div>
        </div>
      </div>
    </div>

    <fl-media-viewer
      #galleryViewer
      *ngIf="isViewerEnabled && galleryItems?.length > 0 && clickedViewer"
      [mode]="'modal'"
      [galleryItems]="galleryItems"
      [downloadable]="isGalleryItemsDownloadable"
      (loaded)="handleLoadedMediaViewer()"
    />
  `,
  styleUrls: ['./gallery-mosaic.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GalleryMosaicComponent implements OnChanges {
  FileDisplayType = FileDisplayType;
  GalleryItemType = GalleryItemType;
  HeadingColor = HeadingColor;
  HeadingType = HeadingType;
  HeadingWeight = HeadingWeight;
  IconSize = IconSize;
  PictureLayout = PictureLayout;
  PictureLoadMethod = PictureLoadMethod;
  PictureObjectFit = PictureObjectFit;
  RoundCornerType = RoundCornerType;
  SpinnerSize = SpinnerSize;
  TextSize = TextSize;

  displayedGalleryItemsModern = 4;

  @Input() centerContent = false;
  @Input() galleryItems: readonly GalleryItem[];

  @Input() textSize?: TextSize;
  @Input() textSizeTablet?: TextSize;
  @Input() textSizeDesktop?: TextSize;
  @Input() zoomOnHover = true;

  @Input() usingFastlyIO = false;
  @Input() maxWidth?: number;
  @Input() imageSizes?: string;

  @Input() isViewerEnabled = false;
  @Input() isGalleryItemsDownloadable = true;

  /** This input only works when `forceRectangleMedium` is applied */
  @Input() reverseGallery = false;

  /** Forces the cover items to be rectangular */
  @Input() forceRectangleMedium = false;

  @Input() interactiveContent = false;

  /** How a single image should fit in the container. */
  @Input() singleImageFit: SingleImageFit = 'cover';

  @Input() pictureObjectFit?: PictureObjectFit;

  /** Rounds the gallery's container or item corners */
  @HostBinding('attr.rounded-corners')
  @Input()
  roundGalleryCorners?: RoundCornerType;

  /** Total spacing between each gallery item */
  @HostBinding('attr.item-spacing')
  @Input()
  itemSpacing: 'xxxsmall' | 'xxsmall' | 'xsmall' = 'xxxsmall';

  /** This variable defines how an image is to be loaded */
  @Input()
  loadMethod?: PictureLoadMethod;

  @Output() openItem = new EventEmitter<number>();

  @ViewChild('galleryViewer', { read: MediaViewerComponent, static: false })
  galleryViewer: MediaViewerComponent;

  @ContentChild(TemplateRef)
  galleryInteractiveContent: TemplateRef<GalleryMosaicInteractiveContentComponent>;

  maxColumnWidth?: number;
  errorItems: readonly GalleryItem[] = [];
  loadingItems: readonly boolean[] = [];

  firstGalleryItemAspectRatio: AspectRatioValue;
  galleryItemAspectRatio: AspectRatioValue;

  isSingleImageGallery = false;
  clickedViewer = false;
  clickedIndex: number | undefined;

  ngOnChanges(changes: SimpleChanges): void {
    if (
      ('maxWidth' in changes || 'galleryItems' in changes) &&
      this.galleryItems
    ) {
      this.maxColumnWidth = this.maxWidth
        ? // Largest image in this layout takes the whole width of the container
          // NOTE: Keep this in sync as the layout changes
          this.galleryItems.length === 1 ||
          this.galleryItems.length === 3 ||
          this.galleryItems.length === 4
          ? this.maxWidth
          : this.maxWidth / 2
        : undefined;
    }

    if ('forceRectangleMedium' in changes) {
      this.displayedGalleryItemsModern = this.forceRectangleMedium ? 5 : 4;
    }

    if ('galleryItems' in changes && this.galleryItems) {
      // Maintain the loading items
      if (this.displayedGalleryItemsModern > this.loadingItems.length) {
        this.loadingItems = [
          ...this.loadingItems,
          ...Array.from(
            new Array(
              this.displayedGalleryItemsModern - this.loadingItems.length,
            ),
            (item, index) =>
              this.galleryItems && this.galleryItems.length > index
                ? this.galleryItems[index].type === GalleryItemType.PICTURE
                : true,
          ),
        ];
      } else {
        this.loadingItems = this.loadingItems.slice(
          0,
          this.displayedGalleryItemsModern,
        );
      }
      switch (this.galleryItems.length) {
        case 1: {
          this.firstGalleryItemAspectRatio = 'rectangle-medium';
          this.galleryItemAspectRatio = 'rectangle-medium';

          const [firstGalleryItem] = this.galleryItems;
          this.isSingleImageGallery =
            firstGalleryItem.type === GalleryItemType.PICTURE;

          break;
        }

        case 2: {
          if (this.forceRectangleMedium) {
            this.firstGalleryItemAspectRatio = 'rectangle-medium';
            this.galleryItemAspectRatio = 'rectangle-medium';
          } else {
            this.firstGalleryItemAspectRatio = 'square';
            this.galleryItemAspectRatio = 'square';
          }
          break;
        }

        case 3: {
          this.firstGalleryItemAspectRatio = 'rectangle-large';
          this.galleryItemAspectRatio = 'rectangle-medium';
          break;
        }

        case 4: {
          this.firstGalleryItemAspectRatio = 'rectangle-large';
          this.galleryItemAspectRatio = this.forceRectangleMedium
            ? 'rectangle-medium'
            : 'square';
          break;
        }

        default:
          this.firstGalleryItemAspectRatio = 'rectangle-medium';

          if (this.forceRectangleMedium) {
            this.galleryItemAspectRatio = 'rectangle-medium';
          } else {
            this.galleryItemAspectRatio = 'square';
          }
      }
    }
  }

  handleGalleryItemClicked(index: number, event: MouseEvent): void {
    this.openItem.emit(index);

    // Lazy load the media viewer.
    if (!this.clickedViewer) {
      this.clickedViewer = true;
      this.clickedIndex = index;
      return;
    }

    // The media viewer is already loaded, so just open the modal.
    this.openMediaViewer(index);
  }

  private openMediaViewer(index: number): void {
    if (this.isViewerEnabled) {
      if (this.galleryViewer.isModalOpen()) {
        this.galleryViewer.goToItem(index);
      } else {
        this.galleryViewer.openModal(index);
      }
    }
  }

  /**
   * We are lazy loading the fl-media-viewer after the user clicks the image.
   * This function is called after the media viewer is loaded. It then attempts to
   * open the media viewer.
   */
  handleLoadedMediaViewer(): void {
    setTimeout(() => {
      if (this.clickedIndex !== undefined) {
        this.openMediaViewer(this.clickedIndex);
        this.clickedIndex = undefined;
      }
    });
  }

  handleImageLoadError(galleryItem: GalleryItem): void {
    this.errorItems = [...this.errorItems, galleryItem];
    const index = this.galleryItems.indexOf(galleryItem);
    this.stopLoading(index);
  }

  stopLoading(index: number): void {
    this.loadingItems = Object.assign([], this.loadingItems, {
      [index]: false,
    });
  }

  trackByItemSource(_: number, item: GalleryItem): string {
    return item.source;
  }
}
